import {
	CASSETTE_CHOCK_INITIAL_VALUES,
	CassetteChock,
} from "@/pages/catalogs/models/CassetteChock";
import { upsertCassetteChock } from "@/pages/catalogs/repositories/CassetteChockRepository";
import { CommentsField } from "@/shared/components/CommentsField";
import { ErrorModal, getMomentTz, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	FormControl,
	FormHelperText,
	Grid2,
	IconButton,
	TextField,
} from "@mui/material";
import { CalendarIcon, DatePicker } from "@mui/x-date-pickers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { LocationTreeSelector } from "./LocationTreeSelector";

const moment = getMomentTz();

type Props = {
	show: boolean;
	onHide: (partId?: number) => void;
};

export const NewCassetteChockDialog = ({ show, onHide }: Props) => {
	const queryClient = useQueryClient();

	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");

	const { control, reset, getValues, trigger } = useForm<CassetteChock>({
		mode: "onChange",
		defaultValues: CASSETTE_CHOCK_INITIAL_VALUES,
	});

	const validateNotInFuture = (description: string, value: Date | null) => {
		if (value && value > new Date()) {
			return `${description} time cannot be in the future.`;
		}
		return true;
	};

	const createCassetteChock = useMutation({
		mutationFn: upsertCassetteChock,
		meta: { successMessage: "Cassette chock created successfully" },
		onSuccess: async (partId) => {
			await queryClient.invalidateQueries({ queryKey: ["cassetteChocks"] });
			onHide(partId);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const handleSave = async () => {
		const isValid = await trigger();
		if (!isValid) return;

		setIsSubmitLoading(true);
		const data = getValues();
		createCassetteChock.mutate({ ...data, PartId: null });
	};

	const handleCancel = () => {
		reset(CASSETTE_CHOCK_INITIAL_VALUES);
		onHide();
	};

	const handleClose = () => {
		reset(CASSETTE_CHOCK_INITIAL_VALUES);
		onHide();
	};

	return (
		<>
			<MesfModal
				open={show}
				handleClose={handleClose}
				title="NEW CASSETTE CHOCK"
				maxWidth="md"
			>
				<MesfModal.Content
					dividers
					sx={{
						padding: (theme) => theme.spacing(2),
						minWidth: 500,
						overflowY: "auto",
					}}
				>
					<Grid2 container spacing={1}>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="PartName"
								control={control}
								rules={{ required: "Name is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										required
										label="Name"
										variant="outlined"
										{...field}
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="LocationId"
								control={control}
								render={({ field }) => (
									<LocationTreeSelector
										label="Location"
										value={field.value}
										onChange={field.onChange}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="RebuildTimestamp"
								control={control}
								shouldUnregister
								rules={{
									validate: {
										notInFuture: (value) =>
											validateNotInFuture("Rebuild", value),
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<FormControl fullWidth error={!!error}>
										<DatePicker
											label="Rebuild Date"
											format="MM/DD/YYYY"
											views={["year", "month", "day"]}
											value={field.value ? moment(field.value) : null}
											onChange={field.onChange}
											slots={{
												openPickerButton: (props) => (
													<IconButton
														id="rebuild-date-calendar-button"
														size="small"
														onClick={props.onClick}
													>
														<CalendarIcon />
													</IconButton>
												),
											}}
											slotProps={{
												textField: {
													size: "small",
													error: !!error,
												},
											}}
										/>
										{error && <FormHelperText>{error.message}</FormHelperText>}
									</FormControl>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="BearingChangeTimestamp"
								control={control}
								shouldUnregister
								rules={{
									validate: {
										notInFuture: (value) =>
											validateNotInFuture("Bearing Change", value),
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<FormControl fullWidth error={!!error}>
										<DatePicker
											label="Bearing Change Date"
											format="MM/DD/YYYY"
											views={["year", "month", "day"]}
											value={field.value ? moment(field.value) : null}
											onChange={field.onChange}
											slots={{
												openPickerButton: (props) => (
													<IconButton
														id="bearing-change-date-calendar-button"
														size="small"
														onClick={props.onClick}
													>
														<CalendarIcon />
													</IconButton>
												),
											}}
											slotProps={{
												textField: {
													size: "small",
													error: !!error,
												},
											}}
										/>
										{error && <FormHelperText>{error.message}</FormHelperText>}
									</FormControl>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="Comments"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										getValues={getValues}
									/>
								)}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>

				<MesfModal.Actions style={{ padding: "15px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3 }}>
							<Button
								variant="contained"
								color="inherit"
								onClick={handleCancel}
								disabled={isSubmitLoading}
								fullWidth
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSave}
								startIcon={
									isSubmitLoading ? (
										<CircularProgress size="1rem" color="inherit" />
									) : null
								}
								disabled={isSubmitLoading}
								fullWidth
							>
								Create
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Creating Cassette Chock"
			/>
		</>
	);
};
